import { inject, Injectable, signal } from '@angular/core';
import { LangGraphService } from '@maximizer/ai/data-access';
import { SHA256 } from 'crypto-es';
import {
  catchError,
  defer,
  EMPTY,
  map,
  Observable,
  of,
  retry,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { AnswerAction } from '../../answer-widgets/answer-widget.model';
import { isRecord } from '../../classes/value-checks';
import {
  AnswerActionWhen,
  SuggestedActionService,
} from './suggested-action.service';

export type SuggestedActionState = 'idle' | 'working' | 'created' | 'failed';

export interface SuggestedActionStatus {
  state: SuggestedActionState;
  key?: string;
  createdAt?: string;
  /** The day, and time, the record was created for: what a created card shows. */
  when?: AnswerActionWhen;
}

/** A card, by the answer it is under: the thread, the fence's hash and the item's place in it. */
export interface SuggestedActionId {
  threadId: string | null;
  /** The fence as written, hashed by {@link fenceHash}. */
  fence: string;
  index: number;
}

/** A created record as its thread keeps it, under {@link savedRecordKey}. */
export interface SuggestedActionSaved {
  key: string;
  createdAt: string;
  /** `when.start`, as an ISO timestamp. */
  start: string;
  hasTime: boolean;
}

/** What each created record's thread-metadata key starts with. */
export const SUGGESTED_ACTIONS_METADATA = 'suggested_actions';

const SAVED_KEY = new RegExp(
  `^${SUGGESTED_ACTIONS_METADATA}:([0-9a-f]{64}):([0-9]+)$`,
);

// A read or a save that fails is tried twice more, a second apart.
const RETRY = { count: 2, delay: 1000 };

const IDLE: SuggestedActionStatus = { state: 'idle' };

type ThreadRead = 'reading' | 'unread';

/** The hash a fence is saved under; the cards take it once per answer. */
export function fenceHash(source: string): string {
  return SHA256(source).toString();
}

/** One key per record, so a save merged into the thread's metadata never overwrites another's. */
export function savedRecordKey({
  fence,
  index,
}: Pick<SuggestedActionId, 'fence' | 'index'>): string {
  return `${SUGGESTED_ACTIONS_METADATA}:${fence}:${index}`;
}

/**
 * What became of each suggested action, this session's and the thread's. It owns the create
 * request, so a dialog closed mid-way still ends in created or failed, and saves what it created.
 */
@Injectable({ providedIn: 'root' })
export class SuggestedActionsStore {
  private readonly service = inject(SuggestedActionService);
  private readonly langGraph = inject(LangGraphService);

  private readonly statuses = signal<
    ReadonlyMap<string, SuggestedActionStatus>
  >(new Map());
  private readonly reads = signal<ReadonlyMap<string, ThreadRead>>(new Map());
  private readonly loads = new Map<string, Observable<void>>();
  // Records whose save failed, saved again after their thread's next read or create.
  private readonly unsaved = new Map<string, SuggestedActionId>();
  private readonly lostSaves = new Subject<SuggestedActionId>();

  /** A created record whose save still failed after its retries; it is tried again later. */
  readonly saveFailures = this.lostSaves.asObservable();

  statusOf(id: SuggestedActionId): SuggestedActionStatus {
    return this.statuses().get(keyOf(id)) ?? IDLE;
  }

  /** Whether the thread's saved records are still being read. */
  restoring(threadId: string | null): boolean {
    return threadId !== null && this.reads().get(threadId) === 'reading';
  }

  /** Whether the thread could not be read, so its cards cannot tell what was already created. */
  unread(threadId: string | null): boolean {
    return threadId !== null && this.reads().get(threadId) === 'unread';
  }

  /** Reads what the thread kept, once a session; a thread that could not be read is read again. */
  restore(threadId: string | null): void {
    if (threadId) {
      this.load(threadId).subscribe({ error: () => undefined });
    }
  }

  /**
   * Creates the record once and emits its key, or null when the CRM refused it. Nothing is created
   * or emitted for a card working or created here or elsewhere, or on a thread not read.
   */
  create(
    id: SuggestedActionId,
    action: AnswerAction,
    when: AnswerActionWhen,
  ): Observable<string | null> {
    const { state } = this.statusOf(id);
    if (
      state === 'working' ||
      state === 'created' ||
      this.restoring(id.threadId) ||
      this.unread(id.threadId)
    ) {
      return EMPTY;
    }

    this.set(id, { state: 'working' });

    const created = this.savedElsewhere(id).pipe(
      switchMap((saved) => {
        if (saved) {
          this.set(id, saved);
          return EMPTY;
        }
        return this.service.create(action, when).pipe(
          catchError(() => of(null)),
          tap((key) => this.settle(id, key, when)),
        );
      }),
      shareReplay(1),
    );
    // Subscribed here, so the outcome lands whether or not the card is still on screen.
    created.subscribe();

    return created;
  }

  // Read again right before the CRM call, as another session may have created the record since;
  // a failed read leaves the earlier one to go by.
  private savedElsewhere({
    threadId,
    fence,
    index,
  }: SuggestedActionId): Observable<SuggestedActionStatus | null> {
    if (!threadId) {
      return of(null);
    }

    const name = savedRecordKey({ fence, index });
    return defer(() => this.langGraph.readThreadMetadata(threadId)).pipe(
      map((metadata) => {
        this.seed(threadId, metadata);
        return metadata && Object.hasOwn(metadata, name)
          ? fromSaved(metadata[name])
          : null;
      }),
      catchError(() => of(null)),
    );
  }

  private settle(
    id: SuggestedActionId,
    key: string | null,
    when: AnswerActionWhen,
  ): void {
    if (!key) {
      this.set(id, { state: 'failed' });
      return;
    }

    this.set(id, {
      state: 'created',
      key,
      createdAt: new Date().toISOString(),
      when,
    });
    if (id.threadId) {
      this.save(id);
      this.resave(id.threadId);
    }
  }

  private set(id: SuggestedActionId, status: SuggestedActionStatus): void {
    this.statuses.update((statuses) =>
      new Map(statuses).set(keyOf(id), status),
    );
  }

  private setRead(threadId: string, read: ThreadRead | null): void {
    this.reads.update((reads) => {
      const next = new Map(reads);
      if (read) {
        next.set(threadId, read);
      } else {
        next.delete(threadId);
      }
      return next;
    });
  }

  private load(threadId: string): Observable<void> {
    let load = this.loads.get(threadId);
    if (!load) {
      this.setRead(threadId, 'reading');
      load = defer(() => this.langGraph.readThreadMetadata(threadId)).pipe(
        retry(RETRY),
        map((metadata) => {
          this.seed(threadId, metadata);
          this.setRead(threadId, null);
          this.resave(threadId);
        }),
        catchError((error: unknown) => {
          this.loads.delete(threadId);
          this.setRead(threadId, 'unread');
          throw error;
        }),
        shareReplay(1),
      );
      this.loads.set(threadId, load);
    }
    return load;
  }

  // A record the thread kept shows as created, unless this session is creating it or created it.
  private seed(
    threadId: string,
    metadata: Record<string, unknown> | null,
  ): void {
    const saved = readSaved(metadata);
    if (saved.length === 0) {
      return;
    }

    this.statuses.update((statuses) => {
      const next = new Map(statuses);
      for (const { fence, index, status } of saved) {
        const key = keyOf({ threadId, fence, index });
        const state = next.get(key)?.state;
        if (state !== 'working' && state !== 'created') {
          next.set(key, status);
        }
      }
      return next;
    });
  }

  private save(id: SuggestedActionId): void {
    const { threadId } = id;
    const record = toSaved(this.statusOf(id));
    if (!threadId || !record) {
      return;
    }

    const key = keyOf(id);
    defer(() =>
      this.langGraph.updateThreadMetadata(threadId, {
        [savedRecordKey(id)]: record,
      }),
    )
      .pipe(retry(RETRY))
      .subscribe({
        complete: () => this.unsaved.delete(key),
        // Reported once per record, when it is first queued, however often it fails again.
        error: () => {
          if (this.unsaved.has(key)) {
            return;
          }
          this.unsaved.set(key, id);
          console.warn(
            '[suggested-actions] a created record could not be saved on its thread',
            threadId,
          );
          this.lostSaves.next(id);
        },
      });
  }

  private resave(threadId: string): void {
    for (const id of this.unsaved.values()) {
      if (id.threadId === threadId) {
        this.save(id);
      }
    }
  }
}

function keyOf({ threadId, fence, index }: SuggestedActionId): string {
  return `${threadId ?? ''}\n${fence}\n${index}`;
}

function toSaved({
  state,
  key,
  createdAt,
  when,
}: SuggestedActionStatus): SuggestedActionSaved | null {
  return state === 'created' && key && when
    ? {
        key,
        createdAt: createdAt ?? new Date().toISOString(),
        start: when.start.toISOString(),
        hasTime: when.hasTime,
      }
    : null;
}

interface SavedEntry {
  fence: string;
  index: number;
  status: SuggestedActionStatus;
}

// Whatever the thread kept that is not a record with a key is left out.
function readSaved(metadata: Record<string, unknown> | null): SavedEntry[] {
  return Object.entries(metadata ?? {}).flatMap(([name, value]) => {
    const match = SAVED_KEY.exec(name);
    const status = match ? fromSaved(value) : null;
    return match && status
      ? [{ fence: match[1], index: Number(match[2]), status }]
      : [];
  });
}

function fromSaved(value: unknown): SuggestedActionStatus | null {
  if (!isRecord(value) || typeof value['key'] !== 'string' || !value['key']) {
    return null;
  }

  const start =
    typeof value['start'] === 'string' ? new Date(value['start']) : null;

  return {
    state: 'created',
    key: value['key'],
    createdAt: typeof value['createdAt'] === 'string' ? value['createdAt'] : '',
    ...(start && !Number.isNaN(start.getTime())
      ? { when: { start, hasTime: value['hasTime'] === true } }
      : {}),
  };
}
